import InlineEditDateTimePicker from '@components/v2/form/InlineEditDateTimePicker/InlineEditDateTimePicker';
import InlineEditField from '@components/v2/form/InlineEditField/InlineEditField';
import InlineEditSelect from '@components/v2/form/InlineEditSelect/InlineEditSelect';
import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import { Stack } from '@mui/material';
import { Ellipsis } from 'lucide-react';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import { Catalog } from '../../../../../../catalog.types';
import CatalogBannersModal from '../../../CatalogBannersModal/CatalogBannersModal';
import useCatalogGeneralInfoActions from './hook/useCatalogGeneralInfoActions';
import useCatalogGeneralInfoSection from './hook/useCatalogGeneralInfoSection';

export type CatalogGeneralInfoSectionProps = Pick<
  Catalog,
  | 'id'
  | 'name'
  | 'code'
  | 'isDefault'
  | 'isDynamic'
  | 'active'
  | 'startDate'
  | 'endDate'
  | 'enablePublicSharing'
  | 'liveLink'
> & {
  isReadOnly: boolean;
};

const CatalogGeneralInfoSection = (props: CatalogGeneralInfoSectionProps) => {
  const { id, name, code, liveLink, isDefault, isReadOnly } = props;
  const { translate } = useTranslation();
  const {
    canShowActions,
    anchorEl,
    isMenuOpen,
    isBannersModalOpen,
    openMenu,
    closeMenu,
    openBannersModal,
    closeBannersModal,
  } = useCatalogGeneralInfoActions({ isReadOnly });
  const {
    readOnlyFields,
    publicSharingValue,
    publicSharingOptions,
    statusValue,
    statusOptions,
    dateFields,
    dateLabels,
    isFieldDisabled,
    isStatusDisabled,
    validateRequired,
    validateLiveLink,
    validateStartDate,
    validateEndDate,
    submit,
    submitPublicSharing,
    submitStatus,
    submitStartDate,
    submitEndDate,
  } = useCatalogGeneralInfoSection(props);

  return (
    <ObjectScreen.Section
      title={LangEnum.GLOBAL_GENERAL_DATA}
      isDefaultOpen
      action={
        canShowActions && (
          <>
            <IconButton
              icon={Ellipsis}
              size="medium"
              testId={CatalogProductsTestIds.DETAILS_GENERAL_DATA_MENU}
              ariaLabel={translate(LangEnum.GLOBAL_MORE_ACTIONS)}
              onClick={(event) => openMenu(event.currentTarget)}
            />

            <Menu
              anchorEl={anchorEl}
              open={isMenuOpen}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              onClose={closeMenu}
            >
              <MenuItem
                testId={CatalogProductsTestIds.DETAILS_MANAGE_BANNERS}
                onClick={openBannersModal}
              >
                {translate(LangEnum.CATALOGS_MANAGE_BANNERS)}
              </MenuItem>
            </Menu>

            <CatalogBannersModal
              catalogId={id}
              isOpen={isBannersModalOpen}
              onClose={closeBannersModal}
            />
          </>
        )
      }
    >
      <Stack sx={{ gap: 0.5 }}>
        <ObjectScreen.Field
          label={LangEnum.GLOBAL_NAME}
          isRequired
          control={
            <InlineEditField
              value={name}
              isDisabled={isFieldDisabled}
              validate={validateRequired}
              testId={CatalogProductsTestIds.DETAILS_GENERAL_DATA_NAME}
              onConfirm={(value) => submit({ name: value })}
            />
          }
        />

        {!isDefault && (
          <>
            <ObjectScreen.Field
              label={LangEnum.GLOBAL_CODE}
              isRequired
              control={
                <InlineEditField
                  value={code}
                  isDisabled={isFieldDisabled}
                  validate={validateRequired}
                  testId={CatalogProductsTestIds.DETAILS_GENERAL_DATA_CODE}
                  onConfirm={(value) => submit({ code: value })}
                />
              }
            />

            <ObjectScreen.Field
              label={LangEnum.GLOBAL_INITIAL_DATE}
              control={
                <InlineEditDateTimePicker
                  value={dateFields.startDate}
                  displayLabel={dateLabels.startDate}
                  maxDateTime={dateFields.endDate ?? undefined}
                  isDisabled={isFieldDisabled}
                  testId={CatalogProductsTestIds.DETAILS_GENERAL_DATA_START_DATE}
                  validate={validateStartDate}
                  onConfirm={submitStartDate}
                />
              }
            />

            <ObjectScreen.Field
              label={LangEnum.GLOBAL_FINAL_DATE}
              control={
                <InlineEditDateTimePicker
                  value={dateFields.endDate}
                  displayLabel={dateLabels.endDate}
                  minDateTime={dateFields.startDate ?? undefined}
                  isDisabled={isFieldDisabled}
                  testId={CatalogProductsTestIds.DETAILS_GENERAL_DATA_END_DATE}
                  validate={validateEndDate}
                  onConfirm={submitEndDate}
                />
              }
            />
          </>
        )}

        {!isDefault && (
          <ObjectScreen.Field label={LangEnum.GLOBAL_TYPE} value={readOnlyFields.type} />
        )}
        <ObjectScreen.Field
          label={LangEnum.GLOBAL_STATUS}
          control={
            <InlineEditSelect
              value={statusValue}
              options={statusOptions}
              isDisabled={isStatusDisabled}
              testId={CatalogProductsTestIds.DETAILS_GENERAL_DATA_STATUS}
              onConfirm={submitStatus}
            />
          }
        />

        {!isDefault && (
          <>
            <ObjectScreen.Field
              label={LangEnum.GLOBAL_PUBLIC_LINK}
              control={
                <InlineEditSelect
                  value={publicSharingValue}
                  options={publicSharingOptions}
                  isDisabled={isFieldDisabled}
                  testId={CatalogProductsTestIds.DETAILS_GENERAL_DATA_PUBLIC_SHARING}
                  onConfirm={submitPublicSharing}
                />
              }
            />

            <ObjectScreen.Field
              label={LangEnum.CATALOG_LIVE_SHOP_BUTTON}
              control={
                <InlineEditField
                  value={liveLink ?? ''}
                  isDisabled={isFieldDisabled}
                  placeholder={LangEnum.CATALOGS_CATALOG_FORM_LINK}
                  testId={CatalogProductsTestIds.DETAILS_GENERAL_DATA_LIVE_LINK}
                  validate={validateLiveLink}
                  onConfirm={(value) => submit({ liveLink: value || null })}
                />
              }
            />
          </>
        )}
      </Stack>
    </ObjectScreen.Section>
  );
};

export default CatalogGeneralInfoSection;
